import { useEffect, useRef, useState } from "react";
import {
  getSalesPoints,
  getUsers,
  newSalesPoint,
  editSalesPoint,
  deleteSalesPoint,
} from "../../../../../api/configuration";
import { getSalesChannels } from "../../../../../api/salesChannels";
import { useDispatch } from "react-redux";
import { requestTableRefresh } from "../../../../../reducers/userConfigSlice";
import "./terminals.css";
const pointId = (row) => row.PointOfSaleConfigurationId;
const channelsOf = (row) => row.SalesChannels || [];
const primaryOf = (row) =>
  channelsOf(row).find(
    (c) =>
      c.SalesChannelsPointsOfSales?.isPrimary === true ||
      c.SalesChannelsPointsOfSales?.isPrimary === 1,
  )?.salesChannelId || "";
const same = (a, b) =>
  JSON.stringify([...a].sort()) === JSON.stringify([...b].sort());
const initial = (row, channelId) => ({
  id: pointId(row),
  name: row.name || "",
  primary: row ? primaryOf(row) : channelId,
  users: (row.assignedUsers || []).map((u) => u.userId),
  channels: channelsOf(row).map((c) => c.salesChannelId),
});
export const ChannelTerminals = ({ channelId, onPending }) => {
  const dispatch = useDispatch();
  const [data, setData] = useState(null),
    [error, setError] = useState(""),
    [attempt, setAttempt] = useState(0);
  const [draft, setDraft] = useState(null),
    [confirmDelete, setConfirmDelete] = useState(null),
    [status, setStatus] = useState(""),
    [uncertain, setUncertain] = useState(false);
  const lock = useRef(false),
    current = useRef(true),
    nameInput = useRef(null);
  const read = async () => {
    const [points, users, channels] = await Promise.all([
      getSalesPoints(),
      getUsers(),
      getSalesChannels(),
    ]);
    if (
      points?.error !== false ||
      !Array.isArray(points.data) ||
      users?.error !== false ||
      !Array.isArray(users.data?.owners) ||
      !Array.isArray(users.data?.corpUsers) ||
      channels?.error !== false ||
      !Array.isArray(channels.data)
    )
      throw Error();
    const availableUsers = [
      ...new Map(
        [
          ...users.data.owners.filter((u) => u.role !== "admin"),
          ...users.data.corpUsers,
        ].map((u) => [u.userId, u]),
      ).values(),
    ];
    return {
      points: points.data,
      users: availableUsers,
      channels: channels.data.filter((c) => c.type !== "ONLINE"),
    };
  };
  useEffect(() => {
    current.current = true;
    let alive = true;
    setError("");
    read()
      .then((next) => {
        if (alive) {
          setData(next);
          setUncertain(false);
        }
      })
      .catch(() => {
        if (alive)
          setError(
            "No se pudieron leer los terminales y usuarios. Reintenta antes de editar.",
          );
      });
    return () => {
      alive = false;
      current.current = false;
    };
  }, [channelId, attempt]);
  const open = (row) => {
    if (lock.current) return;
    setDraft(
      row
        ? initial(row, channelId)
        : {
            id: null,
            name: "",
            primary: channelId,
            users: [],
            channels: [channelId],
          },
    );
    setConfirmDelete(null);
    setStatus("");
    setError("");
  };
  const change = (key, value) =>
    setDraft((previous) => ({ ...previous, [key]: value }));
  const toggle = (key, value) =>
    change(
      key,
      draft[key].includes(value)
        ? draft[key].filter((id) => id !== value)
        : [...draft[key], value],
    );
  const run = async (remove = false) => {
    if (lock.current || !draft || uncertain) return;
    if (!remove && !nameInput.current.reportValidity()) return;
    lock.current = true;
    onPending(true);
    setStatus("");
    setError("");
    let writeOk = false,
      createdId = null;
    try {
      const payload = {
        name: draft.name.trim(),
        userIds: draft.users,
        primarySalesChannelId: draft.primary || null,
        salesChannelsIds: [
          ...new Set([...draft.channels, draft.primary].filter(Boolean)),
        ],
      };
      const response = remove
        ? await deleteSalesPoint(draft.id)
        : draft.id
          ? await editSalesPoint(draft.id, payload)
          : await newSalesPoint(payload);
      writeOk = response?.error === false;
      createdId = response?.data?.PointOfSaleConfigurationId;
      // Re-read even after an uncertain response; never repeat a confirmed creation/deletion.
      const next = await read();
      if (!current.current) return;
      setData(next);
      dispatch(requestTableRefresh());
      if (remove) {
        if (next.points.some((p) => pointId(p) === draft.id)) throw Error();
        setDraft(null);
        setConfirmDelete(null);
        setStatus("Terminal eliminado y ausencia verificada.");
        return;
      }
      const saved = next.points.find(
        (p) => pointId(p) === (draft.id || createdId),
      );
      if (
        !saved ||
        saved.name !== payload.name ||
        primaryOf(saved) !== (payload.primarySalesChannelId || "") ||
        !same(
          (saved.assignedUsers || []).map((u) => u.userId),
          payload.userIds,
        ) ||
        !same(
          channelsOf(saved).map((c) => c.salesChannelId),
          payload.salesChannelsIds,
        )
      )
        throw Error();
      setDraft(null);
      setStatus("Terminal y usuarios guardados y verificados.");
    } catch {
      if (!current.current) return;
      // New IDs cannot be inferred by name, especially after a lost response.
      if (!draft.id || writeOk) {
        setUncertain(true);
        setError(
          "No se pudo confirmar el resultado completo. Usa «Releer terminales» y revisa el listado antes de realizar otra operación; no se repetirá el envío.",
        );
      } else
        setError(
          "No se pudo verificar el cambio. El borrador se conserva; revisa y reintenta.",
        );
    } finally {
      lock.current = false;
      if (current.current) onPending(false);
    }
  };
  if (!data)
    return (
      <div>
        {error ? (
          <p role="alert">
            {error}{" "}
            <button type="button" onClick={() => setAttempt((a) => a + 1)}>
              Reintentar terminales
            </button>
          </p>
        ) : (
          <p role="status">Cargando terminales…</p>
        )}
      </div>
    );
  const associated = data.points.filter((p) =>
    channelsOf(p).some((c) => c.salesChannelId === channelId),
  );
  const others = data.points.filter((p) => !associated.includes(p));
  const cards = (rows) =>
    rows.map((row) => (
      <li key={pointId(row)}>
        <div>
          <strong>{row.name}</strong>
          <p>
            {channelsOf(row).find((c) => c.salesChannelId === primaryOf(row))
              ?.name || "Sin canal principal"}{" "}
            · {(row.assignedUsers || []).length}{" "}
            {(row.assignedUsers || []).length === 1 ? "usuario" : "usuarios"}
          </p>
        </div>
        <button
          type="button"
          aria-label={`Configurar ${row.name}`}
          onClick={() => open(row)}
          disabled={uncertain}
        >
          Configurar
        </button>
      </li>
    ));
  const userOptions = [
    ...data.users,
    ...(draft
      ? data.points.find((p) => pointId(p) === draft.id)?.assignedUsers || []
      : []),
  ].filter(
    (u, index, all) => all.findIndex((x) => x.userId === u.userId) === index,
  );
  return (
    <div className="channelTerminals">
      <p>
        El terminal identifica el puesto al iniciar TPV. Sus usuarios y canales
        visibles se guardan por terminal; un puesto puede aparecer en varias
        fichas de canal.
      </p>
      <ul>{cards(associated)}</ul>
      {!associated.length && <p>No hay terminales asociados a este canal.</p>}
      <button type="button" onClick={() => open(null)} disabled={uncertain}>
        Añadir terminal
      </button>
      {!!others.length && (
        <details>
          <summary>
            Otros terminales y terminales sin canal ({others.length})
          </summary>
          <p>Configúralos para asociarlos a este canal sin crear duplicados.</p>
          <ul>{cards(others)}</ul>
        </details>
      )}
      {draft && (
        <section
          aria-label="Configuración del terminal"
          className="channelTerminalDraft"
        >
          <h4>{draft.id ? "Editar terminal" : "Nuevo terminal"}</h4>
          <label>
            Nombre del terminal
            <input
              ref={nameInput}
              aria-label="Nombre del terminal"
              form="terminal-independent-input"
              value={draft.name}
              onChange={(e) => change("name", e.target.value)}
              required
              pattern={".*\\S.*"}
            />
          </label>
          <label>
            Canal principal del terminal
            <select
              aria-label="Canal principal del terminal"
              value={draft.primary}
              onChange={(e) => change("primary", e.target.value)}
            >
              <option value="">Sin canal principal</option>
              {data.channels.map((c) => (
                <option key={c.salesChannelId} value={c.salesChannelId}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <p>
            Los nuevos pedidos del puesto se asignan al canal principal.
            Cambiarlo afecta también al terminal cuando se accede desde otros
            canales.
          </p>
          <fieldset>
            <legend>Usuarios con acceso al terminal</legend>
            {userOptions.map((u) => (
              <label key={u.userId}>
                <input
                  type="checkbox"
                  checked={draft.users.includes(u.userId)}
                  onChange={() => toggle("users", u.userId)}
                />
                {u.fullname || "Usuario asignado"}
              </label>
            ))}
            {!userOptions.length && <p>No hay usuarios disponibles.</p>}
          </fieldset>
          <fieldset>
            <legend>Canales visibles en el terminal</legend>
            {data.channels.map((c) => (
              <label key={c.salesChannelId}>
                <input
                  type="checkbox"
                  checked={
                    draft.channels.includes(c.salesChannelId) ||
                    draft.primary === c.salesChannelId
                  }
                  disabled={draft.primary === c.salesChannelId}
                  onChange={() => toggle("channels", c.salesChannelId)}
                />
                {c.name}
              </label>
            ))}
          </fieldset>
          <div className="channelTerminalActions">
            <button type="button" disabled={uncertain} onClick={() => run()}>
              Guardar terminal
            </button>
            <button
              type="button"
              onClick={() => {
                setDraft(null);
                setConfirmDelete(null);
              }}
            >
              Descartar cambios del terminal
            </button>
            {draft.id && (
              <button
                type="button"
                disabled={uncertain}
                onClick={() => setConfirmDelete(draft.id)}
              >
                Eliminar terminal
              </button>
            )}
          </div>
          {confirmDelete === draft.id && draft.id && (
            <div role="group" aria-label="Confirmar eliminación del terminal">
              <p>
                Se eliminará el terminal «{draft.name}» completo, con sus
                usuarios asignados y sus asociaciones a todos los canales. Esta
                acción no elimina los usuarios ni los canales.
              </p>
              <button
                type="button"
                disabled={uncertain}
                onClick={() => run(true)}
              >
                Confirmar eliminación del terminal
              </button>
              <button type="button" onClick={() => setConfirmDelete(null)}>
                Conservar terminal
              </button>
            </div>
          )}
        </section>
      )}
      {error && <p role="alert">{error}</p>}
      {status && <p role="status">{status}</p>}
      {uncertain && (
        <button
          type="button"
          onClick={() => {
            setDraft(null);
            setConfirmDelete(null);
            setData(null);
            setAttempt((a) => a + 1);
          }}
        >
          Releer terminales
        </button>
      )}
    </div>
  );
};
